import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingLibraryIcon,
  ComputerDesktopIcon,
  WifiIcon,
  PresentationChartBarIcon,
  DeviceTabletIcon,
  LightBulbIcon,
  SpeakerWaveIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';

const ClassroomSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [classroomTypes, setClassroomTypes] = useState([
    'Salle informatique',
    'Salle théorique',
    'Laboratoire',
    'Atelier',
    'Salle polyvalente',
  ]);
  const [newClassroomType, setNewClassroomType] = useState('');
  const [equipmentOptions, setEquipmentOptions] = useState([
    { id: 'computer', name: 'Ordinateurs', icon: ComputerDesktopIcon },
    { id: 'projector', name: 'Projecteur', icon: PresentationChartBarIcon },
    { id: 'wifi', name: 'WiFi', icon: WifiIcon },
    { id: 'speakers', name: 'Système audio', icon: SpeakerWaveIcon },
    { id: 'tablets', name: 'Tablettes', icon: DeviceTabletIcon },
    { id: 'server', name: 'Serveurs', icon: CircleStackIcon },
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      // Afficher notification de succès
    }, 1000);
  };

  const handleAddClassroomType = () => {
    if (newClassroomType.trim() !== '' && !classroomTypes.includes(newClassroomType.trim())) {
      setClassroomTypes([...classroomTypes, newClassroomType.trim()]);
      setNewClassroomType('');
    }
  };

  const handleRemoveClassroomType = (type) => {
    setClassroomTypes(classroomTypes.filter(t => t !== type));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Paramètres des salles</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Types de salles */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <BuildingLibraryIcon className="h-5 w-5 mr-2 text-blue-400" />
            Types de salles
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {classroomTypes.map((type, index) => (
              <div key={index} className="bg-slate-100/80 dark:bg-slate-800/40 p-3 rounded-lg flex justify-between items-center">
                <span className="text-slate-900 dark:text-slate-50">{type}</span>
                <button 
                  type="button" 
                  className="text-slate-600 dark:text-slate-400 hover:text-red-400"
                  onClick={() => handleRemoveClassroomType(type)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center">
            <input
              type="text"
              placeholder="Nouveau type de salle..."
              className="flex-grow bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-l-lg px-4 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={newClassroomType}
              onChange={(e) => setNewClassroomType(e.target.value)}
            />
            <button
              type="button"
              className="px-4 py-2 bg-blue-500/80 hover:bg-blue-500 rounded-r-lg text-slate-900 dark:text-slate-50 transition-colors"
              onClick={handleAddClassroomType}
            >
              Ajouter
            </button>
          </div>
        </div>

        {/* Équipements */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <ComputerDesktopIcon className="h-5 w-5 mr-2 text-purple-400" />
            Équipements disponibles
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {equipmentOptions.map((equipment) => (
              <div key={equipment.id} className="bg-slate-100/80 dark:bg-slate-800/40 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800/80 mr-3">
                      <equipment.icon className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                    </div>
                    <span className="text-slate-900 dark:text-slate-50 font-medium">{equipment.name}</span>
                  </div>
                  <button
                    type="button"
                    className="text-slate-600 dark:text-slate-400 hover:text-red-400"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

            <div className="bg-slate-100/80 dark:bg-slate-800/40 p-4 rounded-lg border border-dashed border-slate-200 dark:border-slate-600 flex items-center justify-center cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors">
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-6 w-6 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Ajouter un équipement</p>
              </div>
            </div>
          </div>
        </div>

        {/* Règles d'occupation */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <LightBulbIcon className="h-5 w-5 mr-2 text-yellow-400" />
            Règles d'occupation
          </h3>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Temps minimum de réservation (minutes)</label>
                <input
                  type="number"
                  min="15"
                  step="15"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="30"
                />
              </div>
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Temps maximum de réservation (heures)</label>
                <input
                  type="number"
                  min="1"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="8"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Délai minimum de réservation (heures)</label>
                <input
                  type="number"
                  min="0"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="24"
                />
              </div>
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Délai maximum de réservation (jours)</label>
                <input
                  type="number"
                  min="1"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="30"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Restrictions</label>
              <div className="space-y-2">
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Autoriser les réservations le week-end</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Autoriser les réservations en soirée (après 18h)</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded" 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Permettre le chevauchement des réservations</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Notification en cas de réservation multiple</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Capacité */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Capacités par défaut
          </h3>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Salle informatique</label>
                <div className="flex">
                  <input
                    type="number"
                    min="1"
                    className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    defaultValue="20"
                  />
                  <span className="inline-flex items-center px-3 bg-slate-50 dark:bg-slate-800/80 rounded-r-lg border border-l-0 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400">
                    personnes
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Salle théorique</label>
                <div className="flex">
                  <input
                    type="number"
                    min="1"
                    className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    defaultValue="30"
                  />
                  <span className="inline-flex items-center px-3 bg-slate-50 dark:bg-slate-800/80 rounded-r-lg border border-l-0 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400">
                    personnes
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Laboratoire</label>
                <div className="flex">
                  <input
                    type="number"
                    min="1"
                    className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    defaultValue="15"
                  />
                  <span className="inline-flex items-center px-3 bg-slate-50 dark:bg-slate-800/80 rounded-r-lg border border-l-0 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400">
                    personnes
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Atelier</label>
                <div className="flex">
                  <input
                    type="number"
                    min="1"
                    className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    defaultValue="12"
                  />
                  <span className="inline-flex items-center px-3 bg-slate-50 dark:bg-slate-800/80 rounded-r-lg border border-l-0 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400">
                    personnes
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Salle polyvalente</label>
              <div className="flex">
                <input
                  type="number"
                  min="1"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="40"
                />
                <span className="inline-flex items-center px-3 bg-slate-50 dark:bg-slate-800/80 rounded-r-lg border border-l-0 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400">
                  personnes
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg text-white font-medium shadow hover:from-purple-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 flex items-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-slate-900 dark:text-slate-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </>
            ) : 'Enregistrer les modifications'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default ClassroomSettings;